import {
    formatCurrencyInputOnChange,
    formatCurrencyInputOnChangeWithoutDecimals,
} from '@fruitful/fruitils';
import type {
    Control,
    FieldErrors,
    FieldValues,
    Path,
    UseFormRegister,
} from 'react-hook-form';
import type { StackProps } from 'tamagui';

import { DollarInput } from '../inputs/DollarInput';
import type { InputProps } from '../inputs/Input';
import {
    ControlledFormattedInput,
    type FormatterFunction,
} from './ControlledFormattedInput';

const createCurrencyFormatter = (
    allowDecimals: boolean = true,
): FormatterFunction => {
    return (input: string, previousValue?: string) => {
        if (input.startsWith('$')) {
            return {
                displayValue: input,
                storedValue: input,
            };
        }

        // Extract digits and decimal point to check if input is empty
        const digitsOnly = input.replace(/[^\d.]/g, '');
        if (digitsOnly === '') {
            // Return empty when empty so placeholder shows in grey
            return {
                displayValue: '',
                storedValue: '',
            };
        }

        const prevValue = previousValue ?? '$ ';
        const formattedValue = allowDecimals
            ? formatCurrencyInputOnChange(prevValue, input)
            : formatCurrencyInputOnChangeWithoutDecimals(prevValue, input);
        return {
            displayValue: formattedValue,
            storedValue: formattedValue,
        };
    };
};

interface ControlledDollarInputProps<
    TFieldValues extends FieldValues,
    TContext,
> extends Omit<InputProps, 'formatValueOnChange'> {
    name: Path<TFieldValues>;
    control: Control<TFieldValues, TContext>;
    register: UseFormRegister<TFieldValues>;
    errors: FieldErrors<TFieldValues>;
    allowDecimals?: boolean;
    label?: string;
    hint?: string;
    containerStyle?: StackProps;
    rightSideContent?: React.ReactNode;
}

export const ControlledDollarInput = <
    TFieldValues extends FieldValues,
    TContext,
>({
    allowDecimals = true,
    ...props
}: ControlledDollarInputProps<TFieldValues, TContext>) => {
    return (
        <ControlledFormattedInput
            {...props}
            formatter={createCurrencyFormatter(allowDecimals)}
            InputComponent={DollarInput}
            inputComponentProps={{
                placeholder: props.placeholder ?? '$',
                allowDecimals,
            }}
        />
    );
};
